import React, { useEffect, useState, useMemo } from "react";
import { Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import Select from 'react-select';
import { IoMdClose } from "react-icons/io";
import { authHeader } from "../../../services/auth-header";
import { BASE_URL } from "../../../services/Api";
import debounce from 'lodash/debounce';
import CommonPumpSpareField from "./CommonPumpSpareField";

const PETOPOPSIConvert = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [productSearch, setProductSearch] = useState(encodeURIComponent(" "));

    // Common PO Fields State
    const [poData, setPoData] = useState(null);

    // Product Table State
    const [grandtotalinr, setGrandTotalInr] = useState(0);
    const [grandtotalcurrency, setGrandTotalCurrency] = useState(0);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [rows, setRows] = useState([]);
    const [productOptions, setProductOptions] = useState([]);

    const unitOptions = [
        { value: "", label: "Select UOM" },
        { value: "bot", label: "Bot" },
        { value: "ctn", label: "Ctn" },
        { value: "days", label: "Days" },
        { value: "ft", label: "Ft" },
        { value: "hrs", label: "Hrs" },
        { value: "kg", label: "Kg" },
        { value: "ltr", label: "Ltr" },
        { value: "mtr", label: "Mtr" },
        { value: "nos", label: "Nos" },
        { value: "pil", label: "Pil" },
        { value: "pkt", label: "Pkt" },
        { value: "set", label: "Set" },
        { value: "pcs", label: "Pcs" }
    ];


    const currencyOptions = [
        { value: "", label: "Select Currency" },
        { value: "$ (USD)", label: "USD" },
        { value: "€ (EUR)", label: "EUR" },
        { value: "¥ (JPY)", label: "JPY" },
        { value: "£ (GBP)", label: "GBP" },
        { value: "₹ (INR)", label: "INR" },


    ];

    const [podate, setpodate] = useState('');
    const [pono, setpono] = useState('');
    const [enquiryValidationMsg, setEnquiryValidationMsg] = useState('');
    const [isEnquiryValid, setIsEnquiryValid] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);

    const checkpono = async (number) => {
        if (!number) {
            setEnquiryValidationMsg('');
            setIsEnquiryValid(false);
            return;
        }

        if (!/^[A-Za-z0-9-]+$/.test(number)) {
            setEnquiryValidationMsg('❌ Only alphanumeric characters and hyphens are allowed');
            setIsEnquiryValid(false);
            return;
        }

        try {
            const response = await axios.get(`${BASE_URL}purchaseOrder/check/${number}`, {
                headers: authHeader(),
            });

            if (response.data === true) {
                setEnquiryValidationMsg('❌ This purchase order number already exists.');
                setIsEnquiryValid(false);
            } else if (response.data === false) {
                setEnquiryValidationMsg('✅ This purchase order number is available.');
                setIsEnquiryValid(true);
            } else {
                setEnquiryValidationMsg('Unexpected response from server.');
                setIsEnquiryValid(false);
            }
        } catch (error) {
            console.error("Error checking purchase order number:", error);
            setEnquiryValidationMsg('❌ Error validating purchase order number.');
            setIsEnquiryValid(false);
        }
    };


    useEffect(() => {
        const debouncedCheck = debounce(() => {
            checkpono(pono);
        }, 400);
        debouncedCheck();

        return () => debouncedCheck.cancel();
    }, [pono]);

    // Fetch PO data by ID
    const fetchPOData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}purchaseEnquiry/findById/${id}`, {
                headers: authHeader()
            });

            const data = response.data;

            // Transform supplier data
            const supplierData = data.supplier ? {
                value: data.supplier.id,
                label: data.supplier.companyname || data.supplier.name,
                contactperson: data.contactperson,
                contactno: data.contactno,
                email: data.email
            } : null;

            // Transform address data
            const addressData = data.supplierMultipleAddress ? {
                value: data.supplierMultipleAddress.id,
                label: data.supplierMultipleAddress.supplieraddress || data.supplierMultipleAddress.address
            } : null;

            // Transform branch data
            const branchData = data.branch ? {
                value: data.branch.id,
                label: data.branch.branchname || data.branch.name
            } : null;

            // Transform warehouse data
            const warehouseData = data.warehouse ? {
                value: data.warehouse.id,
                label: data.warehouse.warehousename || data.warehouse.name
            } : null;

            // Prepare the PO data object
            const transformedPoData = {

                enquiryno: data.enquiryno || '',
                enquirydate: data.enquirydate || '',
                gst: data.gst || '',

                status: data.status || '',
                shipto: data.shipto || '',
                freight: data.freight || '',
                pandf: data.pandf || '',
                paymentterms: data.paymentterms || '',
                deliveryterm: data.deliveryterm || '',
                supplier: supplierData,
                supplierDetails: {
                    contactperson: data.contactperson || '',
                    contactno: data.contactno || '',
                    email: data.email || ''
                },
                supplierMultipleAddress: addressData,
                branch: branchData,
                warehouse: warehouseData
            };

            setPoData(transformedPoData);

            // Set product rows
            if (data.purchaseEnquiryItems && data.purchaseEnquiryItems.length > 0) {
                const formattedRows = data.purchaseEnquiryItems.map(item => ({
                    id: item.id,
                    product: {
                        id: item.product.id,
                        name: `${item.product.description} - ${item.product.serialnumber} - ${item.product.partno}`
                    },
                    description: item.product.description,
                    quantity: item.quantity,
                    exchangerate: item.exchangerate || 1,
                    unitrateinr: item.unitrateinr || 0,
                    totalinr: item.totalinr || 0,
                    totalcurrency: item.totalcurrency || 0,
                    uom: item.uom || unitOptions[0].value,
                    currency: item.currency || currencyOptions[0].value,
                    unitratecurrency: item.unitratecurrency || 0,
                    hsnsacno: item.hsnsacno || item.product.hsnsacno || '',
                    itemcode: item.itemcode || item.product.itemcode || '',
                    serialnumber: item.serialnumber || item.product.serialnumber || '',
                    modelnumber: item.modelnumber || item.product.modelnumber || '',
                    tagnumber: item.tagnumber || item.product.tagnumber || '',
                    moc: item.moc || item.product.moc || '',
                    partno: item.partno || item.product.partno || '',
                    productremarks: item.productremarks || ''
                }));
                setRows(formattedRows);

                // Set grand totals
                setGrandTotalInr(data.grandtotalinr || 0);
                setGrandTotalCurrency(data.grandtotalcurrency || 0);
            }
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data || 'Failed to fetch PO data' });
        } finally {
            setIsLoading(false);
        }
    };


    // Fetch initial data
    useEffect(() => {
        fetchPOData();
    }, [id]);

    // Debounced product search
    const handleProductSearchChange = debounce((inputValue) => {
        setProductSearch(inputValue ? encodeURIComponent(inputValue) : encodeURIComponent(" "));
    }, 500);

    // Fetch product data
    const getProductData = async () => {
        try {
            const response = await axios.get(
                `${BASE_URL}product/page/ptype/0/10/desc/id/2/Finished,Raw Material, Semi Finished,Services/${productSearch}`,
                { headers: authHeader() }
            );

            const products = response.data.Index || response.data.data || [];
            setProductOptions(
                products.map(product => ({
                    value: product.id,
                    label: `${product.description} - ${product.serialnumber} - ${product.partno}`,
                    description: product.description,
                    itemcode: product.itemcode,
                    hsnsacno: product.hsnsacno,
                    serialnumber: product.serialnumber,
                    modelnumber: product.modelnumber,
                    tagnumber: product.tagnumber,
                    moc: product.moc,
                    partno: product.partno,
                    uom: product.unitofmeasurement || unitOptions[0].value,
                    exchangerate: product.exchangerate || 1
                }))
            );
        } catch (error) {
            console.error("Error fetching products", error);
        }
    };

    // Handle product selection
    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        const selectedProductData = productOptions.find(product => product.value === selectedOption.value);

        if (selectedProductData) {
            const newRow = {
                product: { id: selectedOption.value, name: selectedOption.label },
                description: selectedProductData.description,
                quantity: '',
                exchangerate: selectedProductData.exchangerate || 1,
                unitrateinr: 0,
                totalinr: 0,
                totalcurrency: 0,
                uom: selectedProductData.uom,
                currency: currencyOptions[0].value,
                unitratecurrency: '',
                hsnsacno: selectedProductData.hsnsacno,
                itemcode: selectedProductData.itemcode,
                serialnumber: selectedProductData.serialnumber,
                modelnumber: selectedProductData.modelnumber,
                tagnumber: selectedProductData.tagnumber,
                moc: selectedProductData.moc,
                partno: selectedProductData.partno,
                productremarks: ''
            };
            setRows([...rows, newRow]);
        }
    };

    // Handle row updates
    const handleRowChange = (index, key, value) => {
        const updatedRows = [...rows];
        updatedRows[index][key] = value;

        // Recalculate totals if relevant fields change
        if (['quantity', 'unitratecurrency', 'exchangerate', 'currency'].includes(key)) {
            const quantity = parseFloat(updatedRows[index].quantity) || 0;
            const unitratecurrency = parseFloat(updatedRows[index].unitratecurrency) || 0;
            const exchangerate = parseFloat(updatedRows[index].exchangerate) || 1;

            updatedRows[index].unitrateinr = unitratecurrency * exchangerate;
            updatedRows[index].totalinr = quantity * updatedRows[index].unitrateinr;
            updatedRows[index].totalcurrency = quantity * unitratecurrency;
        }

        setRows(updatedRows);
    };

    const removeRow = (index) => {
        const updatedRows = [...rows];
        updatedRows.splice(index, 1);
        setRows(updatedRows);
    };

    // Calculate grand totals whenever rows change
    useEffect(() => {
        const totals = rows.reduce((acc, row) => {
            return {
                inr: acc.inr + (row.totalinr || 0),
                currency: acc.currency + (row.totalcurrency || 0)
            };
        }, { inr: 0, currency: 0 });

        setGrandTotalInr(totals.inr);
        setGrandTotalCurrency(totals.currency);
    }, [rows]);


    const validateForm = () => {
        if (!poData.supplier) {
            Swal.fire("Missing Supplier", "Please select a supplier before submitting the form.", "error");
            return false;
        }

        if (!poData.supplierMultipleAddress) {
            Swal.fire("Missing Address", "Please select the supplier's address.", "error");
            return false;
        }

        if (!poData.branch) {
            Swal.fire("Missing Branch", "Please select a branch.", "error");
            return false;
        }

        if (!poData.warehouse) {
            Swal.fire("Missing Warehouse", "Please select a warehouse.", "error");
            return false;
        }

        if (rows.length === 0) {
            Swal.fire("No Products Added", "Please add at least one product to continue.", "error");
            return false;
        }

        for (const row of rows) {
            if (!row.quantity || isNaN(row.quantity) || row.quantity <= 0) {
                Swal.fire(
                    "Invalid Quantity",
                    "Each product must have a valid quantity greater than 0.",
                    "error"
                );
                return false;
            }
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!isEnquiryValid) {
            Swal.fire({
                icon: 'error',
                title: 'Validation Error',
                text: 'Please enter a valid purchase order number before submitting.',
            });
            return;
        }

        if (!validateForm()) return;
        setIsLoading(true);


        const purchaseData = {
            id: id, // Include the ID for update
            purchaseOrderItems: rows.map(row => ({
                id: row.id, // Include item ID if it exists (for updates)
                product: { id: row.product.id },
                exchangerate: row.exchangerate || 0,
                quantity: row.quantity || 0,
                unitrateinr: row.unitrateinr || 0,
                totalinr: row.totalinr || 0,
                totalcurrency: row.totalcurrency || 0,
                uom: row.uom || '',
                currency: row.currency,
                unitratecurrency: row.unitratecurrency || 0,
                productremarks: row.productremarks || '',
                itemcode: row.itemcode || '',
                hsnsacno: row.hsnsacno || '',
                serialnumber: row.serialnumber || '',
                modelnumber: row.modelnumber || '',
                tagnumber: row.tagnumber || '',
                moc: row.moc || '',
                partno: row.partno || '',
            })),
            grandtotalinr,
            grandtotalcurrency,
            potype: 'Pump Spare',
            pocategory: 'International',
            pono,
            podate,
            ...poData,
            contactno: poData.supplierDetails.contactno,
            contactperson: poData.supplierDetails.contactperson,
            email: poData.supplierDetails.email,
            supplier: { id: poData.supplier.value },
            supplierMultipleAddress: poData.supplierMultipleAddress ? { id: poData.supplierMultipleAddress.value } : null,
            branch: poData.branch ? { id: poData.branch.value } : null,
            warehouse: poData.warehouse ? { id: poData.warehouse.value } : null
        };

        try {
            await axios.post(`${BASE_URL}purchaseOrder/purchaseEnquiryToPo/${id}`, purchaseData, {
                headers: authHeader()
            });

            Swal.fire({ icon: 'success', title: 'Success', text: 'Converted successfully!' });
            navigate("/poindex/1");
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: error.response?.data || 'Failed to update PO' });
        } finally {
            setIsLoading(false);
        }
    };





    // Fetch products when search term changes
    useEffect(() => {
        getProductData();
    }, [productSearch]);


    if (isLoading) {
        return (
            <div className="d-flex justify-content-center mt-5">
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    if (!poData) {
        return <div className="alert alert-danger mt-3">Failed to load purchase order data</div>;
    }

    return (
        <div className="container">
            <br />
            <Form>
                <Row>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Purchase Order Number: *</Form.Label>
                            <Form.Control
                                type="text"
                                value={pono}
                                onChange={e => {
                                    setpono(e.target.value);
                                    setFormSubmitted(false);
                                }}
                                isInvalid={
                                    (formSubmitted && !isEnquiryValid) ||
                                    enquiryValidationMsg.includes('exists') ||
                                    enquiryValidationMsg.includes('Error') ||
                                    enquiryValidationMsg.includes('allowed')
                                }
                                required
                            />
                            {enquiryValidationMsg && (
                                <Form.Control.Feedback type={
                                    enquiryValidationMsg.includes('exists') ||
                                        enquiryValidationMsg.includes('Error') ||
                                        enquiryValidationMsg.includes('allowed') ?
                                        "invalid" : "valid"
                                }>
                                    {enquiryValidationMsg}
                                </Form.Control.Feedback>
                            )}
                            {formSubmitted && !isEnquiryValid && !enquiryValidationMsg && (
                                <Form.Control.Feedback type="invalid">
                                    Please enter a valid purchase order number
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>Purchase Order Date</Form.Label>
                            <Form.Control
                                type="date"
                                value={podate}
                                onChange={e => setpodate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <CommonPumpSpareField
                    initialData={poData}
                    onDataChange={setPoData}
                />

                <hr />
                <br />

                {/* Product Selection */}
                <Row>
                    <Col md='12'>
                        <Form.Group>
                            <Form.Label>Select Product</Form.Label>
                            <Select
                                value={selectedProduct}
                                onChange={handleProductChange}
                                options={productOptions}
                                placeholder="Search product..."
                                onInputChange={handleProductSearchChange}
                                isClearable
                                isLoading={isLoading}
                            />
                        </Form.Group>
                    </Col>
                </Row>
                <br />
                <hr />
                <div className="table-responsive">
                    <Table striped bordered hover className="product-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Product Description</th>
                                <th>Serial No.</th>
                                <th>Model No.</th>
                                <th>Tag No.</th>
                                <th>Part No.</th>
                                <th>MOC</th>
                                <th>ITEM CODE</th>
                                <th>HSN/SAC</th>
                                <th>unit of measurement</th>
                                <th>Currency FC</th>
                                <th>Quantity</th>
                                <th>Unit Rate</th>
                                <th>Total (FC)</th>
                                <th>Ex. Rate</th>
                                <th>Unit Rate (₹)</th>
                                <th>Total (₹)</th>
                                <th>Remarks</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        {row.description}
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={row.productremarks}
                                            onChange={(e) => handleRowChange(index, 'productremarks', e.target.value)}
                                            placeholder="Remarks"
                                            className="mt-1"
                                        />
                                    </td>
                                    <td>{row.serialnumber || '-'}</td>
                                    <td>{row.modelnumber || '-'}</td>
                                    <td>{row.tagnumber || '-'}</td>
                                    <td>{row.partno || '-'}</td>
                                    <td>{row.moc || '-'}</td>
                                    <td>{row.itemcode || '-'}</td>
                                    <td>{row.hsnsacno || '-'}</td>
                                    <td>
                                        <Select
                                            value={unitOptions.find(opt => opt.value === row.uom)}
                                            onChange={(e) => handleRowChange(index, 'uom', e.value)}
                                            options={unitOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Select
                                            value={currencyOptions.find(opt => opt.value === row.currency)}
                                            onChange={(e) => handleRowChange(index, 'currency', e.value)}
                                            options={currencyOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                menuPortal: base => ({ ...base, zIndex: 9999 })
                                            }}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            value={row.quantity}
                                            onChange={(e) => handleRowChange(index, 'quantity', e.target.value)}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={row.unitratecurrency}
                                            onChange={(e) => handleRowChange(index, 'unitratecurrency', e.target.value)}
                                        />
                                    </td>
                                    <td>{(row.unitratecurrency * row.quantity).toFixed(2)}</td>
                                    <td>
                                        <Form.Control
                                            type="number"
                                            min="0"
                                            step="0.0001"
                                            value={row.exchangerate}
                                            onChange={(e) => handleRowChange(index, 'exchangerate', e.target.value)}
                                        />
                                    </td>
                                    <td>{(row.unitratecurrency * row.exchangerate).toFixed(2)}</td>
                                    <td>{(row.unitratecurrency * row.exchangerate * row.quantity).toFixed(2)}</td>
                                    <td>{row.productremarks || '-'}</td>
                                    <td>
                                        <Button variant="danger" size="sm" onClick={() => removeRow(index)}>
                                            <IoMdClose />
                                        </Button>
                                    </td>
                                </tr>
                            ))}

                            {/* Grand Total Row */}
                            <tr className="grand-total">
                                <td colSpan="12"></td>
                                <td><strong>Total (FC):</strong></td>
                                <td><strong>{grandtotalcurrency.toFixed(2)}</strong></td>
                                <td></td>
                                <td><strong>Total (₹):</strong></td>
                                <td><strong>{grandtotalinr.toFixed(2)}</strong></td>
                                <td colSpan="2"></td>
                            </tr>
                        </tbody>
                    </Table>
                </div>

                <br />
                <button
                    className="btn btn-primary"
                    onClick={handleSubmit}
                    disabled={isLoading}
                >
                    {isLoading ? <Spinner size="sm" /> : 'CONVERT'}
                </button>
            </Form>
        </div>
    );
};

export default PETOPOPSIConvert;

